import { Component, OnDestroy, ElementRef } from '@angular/core';
import { NgStyle } from '@angular/common';
import {
  FormGroup,
  Validators,
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { NgxMaskDirective } from 'ngx-mask';
// Third Party
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
// functions
import { calculateFFLExpirationDate } from 'src/app/shared/functions';
// models
import {
  ModalID,
  ModalType,
  FFLMonthCharacters,
  EmailType,
} from 'src/app/shared/models/constants';

import { AddEditFFLResponse, Email } from 'src/app/shared/models/general';
import { FFL } from 'src/app/shared/models/general';
// Services
import { SharedService } from 'src/app/shared/services/shared.service';

@Component({
    selector: 'app-ffl-management-edit',
    templateUrl: './ffl-management-edit.component.html',
    styleUrls: ['./ffl-management-edit.component.scss'],
    standalone: true,
    imports: [ReactiveFormsModule, NgxMaskDirective, NgStyle]
})
export class FflManagementEditComponent {
  add_edit_ffl_form: FormGroup;
  modal_type: string;
  selected_row: FFL = new FFL();
  is_ffl_expired: boolean = false;
  certified_ffl_pdf_file?: File;
  certified_ffl_pdf_name: string | null = null;
  removed_existing_pdf: boolean = false;

  duplicate_enabled_entry = true;
  duplicate_entry = true;

  // Modals
  modalRef: BsModalRef;
  modalConfig = { animated: true };

  constructor(
    protected element: ElementRef,
    private modalService: BsModalService,
    private fb: FormBuilder,
    private sharedService: SharedService
  ) {
    // Initializing Form
    this.add_edit_ffl_form = this.fb.group({
      ffl: new FormControl({ value: null, disabled: false }, [
        Validators.required,
      ]),
      legal_name: new FormControl({ value: null, disabled: false }, [
        Validators.required,
      ]),
      business_name: new FormControl({ value: null, disabled: false }),
      phone_number: new FormControl({ value: null, disabled: false }),
      email: new FormControl({ value: null, disabled: false }),
      notes: new FormControl({ value: null, disabled: false }),
      expiration_date: new FormControl({ value: null, disabled: true }),
    });
  }
  ngOnInit(): void {
    if (this.modal_type === ModalType.edit) {
      this.add_edit_ffl_form.patchValue({
        ffl: this.selected_row.ffl,
        legal_name: this.selected_row.legal_name,
        business_name: this.selected_row.business_name,
        phone_number: this.selected_row.phone_number,
        email: this.selected_row.email,
        notes: this.selected_row.notes,
        expiration_date: this.selected_row.expiration_date,
      });
      // Pre-populate existing PDF name if present
      if (this.selected_row.certified_ffl_blob_name) {
        this.certified_ffl_pdf_name = 'Existing Certified FFL.pdf';
      }
    }
    this.isFflExpired();
  }
  closeModalClick(): void {
    this.add_edit_ffl_form.reset();
    this.selected_row = new FFL();
    this.modalService.hide(ModalID.firearm_management_edit);
  }
  addEditFFLClick() {
    const rawFfl = (this.add_edit_ffl_form.value.ffl || '').replace(/-/g, '');
    if (rawFfl.length >= 8) {
      const first3 = rawFfl.substring(0, 3);
      const last5 = rawFfl.substring(rawFfl.length - 5);
      
      const duplicate = this.sharedService.ffl_list.find(existing => {
        if (!existing.ffl) return false;
        const existingRaw = existing.ffl.replace(/-/g, '');
        if (existingRaw.length < 8) return false;
        return existingRaw.substring(0, 3) === first3 && existingRaw.substring(existingRaw.length - 5) === last5;
      });

      if (duplicate && (this.modal_type === ModalType.add || duplicate.id !== this.selected_row?.id)) {
        this.sharedService.raiseErrorToast('This FFL already exists in your list.');
        return;
      }
    }

    let return_data: AddEditFFLResponse = new AddEditFFLResponse();
    if (this.modal_type === ModalType.edit) {
      return_data.modal_type = ModalType.edit;
    } else {
      return_data.modal_type = ModalType.add;
    }
    this.allEntriesToUpperCase();
    return_data.form_values = this.add_edit_ffl_form.value;
    return_data.form_values.expiration_date =
      this.add_edit_ffl_form.get('expiration_date').value;
    if (this.modal_type === ModalType.edit) {
      return_data.form_values.id = this.selected_row.id;
    }
    // Attach the PDF file if selected
    if (this.certified_ffl_pdf_file) {
      return_data.certified_ffl_pdf_file = this.certified_ffl_pdf_file;
    } else if (this.removed_existing_pdf) {
      return_data.remove_certified_ffl_pdf = true;
    }
    this.sharedService.ffl_to_add$.next(return_data);
    this.closeModalClick();
  }
  onCertifiedFflFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const file = input.files[0];
    if (file.type !== 'application/pdf') {
      this.sharedService.raiseWarningToast('Only PDF files are accepted for Certified FFL.');
      input.value = '';
      return;
    }
    this.certified_ffl_pdf_name = file.name;
    this.certified_ffl_pdf_file = file;
    this.removed_existing_pdf = false;
    this.add_edit_ffl_form.markAsDirty();
  }
  removeCertifiedFflPdf(): void {
    this.certified_ffl_pdf_file = undefined;
    if (this.selected_row?.certified_ffl_blob_name && this.certified_ffl_pdf_name === 'Existing Certified FFL.pdf') {
      this.removed_existing_pdf = true;
    }
    this.certified_ffl_pdf_name = null;
    this.add_edit_ffl_form.markAsDirty();
  }
  async viewExistingPdf() {
    if (!this.selected_row?.id || this.certified_ffl_pdf_file) return;
    try {
      const url = await this.sharedService.getCertifiedFflPdfUrl(this.selected_row.id);
      window.open(url, '_blank');
    } catch (e) {
      this.sharedService.raiseErrorToast('Could not load PDF link.');
    }
  }

  async onFflParserFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const file = input.files[0];
    
    // Clear input so they can re-select the same file if needed
    input.value = '';

    this.sharedService.openStatusSpinner('Parsing FFL...');
    try {
      const parsedData = await this.sharedService.parseFflDocument(file);
      
      // Patch form with parsed data
      this.add_edit_ffl_form.patchValue({
        ffl: parsedData.ffl_number || null,
        legal_name: parsedData.legal_name || null,
        business_name: parsedData.business_name || null,
        expiration_date: parsedData.expiration_date || null
      });

      // Verify FFL mask/format updates
      this.verifyFFL();
      this.isFflExpired();

      // Automatically attach the file as the Certified FFL PDF
      this.certified_ffl_pdf_name = file.name;
      this.certified_ffl_pdf_file = file;
      this.removed_existing_pdf = false;
      this.add_edit_ffl_form.markAsDirty();

      this.sharedService.raiseSuccessToast('FFL parsed successfully.');
    } catch (e: any) {
      this.sharedService.raiseErrorToast(e?.error?.message || 'Failed to parse FFL document.');
    } finally {
      this.sharedService.closeStatusSpinner();
    }
  }

  verifyFFL() {
    const new_ffl = this.add_edit_ffl_form.value.ffl.toUpperCase();
    if (
      new_ffl?.length === 20 &&
      this.add_edit_ffl_form.get('expiration_date').value === null
    ) {
      if (FFLMonthCharacters.includes(new_ffl.substring(13, 14))) {
        for (const ffl of this.sharedService.ffl_list) {
          if (
            ffl.ffl.substring(0, 4) === new_ffl.substring(0, 4) &&
            ffl.ffl.substring(15, 20) === new_ffl.substring(15, 20)
          ) {
            if (this.modal_type !== ModalType.edit) {
              this.add_edit_ffl_form.patchValue({
                ffl: null,
                expiration_date: null,
              });
              this.sharedService.raiseWarningToast(
                'FFL already exists in the system.'
              );
              break;
            }
          }
        }
        this.add_edit_ffl_form.patchValue({
          expiration_date: calculateFFLExpirationDate(new_ffl),
        });
        this.isFflExpired();
      } else {
        this.sharedService.raiseErrorToast(
          'FFL Month is not valid. Please enter a valid FFL.'
        );
        this.add_edit_ffl_form.patchValue({
          ffl: null,
          expiration_date: null,
        });
      }
    } else if (
      new_ffl?.length < 20 &&
      this.add_edit_ffl_form.get('expiration_date').value !== null
    ) {
      this.add_edit_ffl_form.patchValue({
        expiration_date: null,
      });
    }
  }
  allEntriesToUpperCase() {
    const controls = this.add_edit_ffl_form.controls;
    for (const name in controls) {
      if (controls.hasOwnProperty(name)) {
        const control = controls[name];
        if (control.value) {
          control.setValue(control.value.toUpperCase());
        }
      }
    }
  }
  isFflExpired() {
    const expiration_date = this.add_edit_ffl_form.get('expiration_date').value;
    if (expiration_date) {
      const today = new Date();
      const expiration = new Date(expiration_date);
      if (expiration < today) {
        this.is_ffl_expired = true;
      } else {
        this.is_ffl_expired = false;
      }
    }
  }
  RequestNewFFL() {
    const email = new Email();
        email.to = this.add_edit_ffl_form.get('email').value;
        email.subject = this.sharedService.company_info.email_templates['ffl_request']['subject'];
        email.body = this.sharedService.company_info.email_templates['ffl_request']['body'];
        const email_type = EmailType.ffl;
        this.sharedService.openEmailModal(email_type, email);
  }
}
